// components/TercerosForm.jsx
import { useEffect, useMemo, useState, useCallback } from 'react';
import api from '../services/api';
import { useLocation, useNavigate } from 'react-router-dom';
import ModalTercero from './ModalTercero';

import { DataGridPremium } from '@mui/x-data-grid-premium';
import { esES } from '@mui/x-data-grid/locales';

function TercerosForm() {
  const location = useLocation();
  const navigate = useNavigate();

  const query = new URLSearchParams(location.search);
  const tipo = query.get('tipo') || 'cliente'; // 'cliente' | 'proveedor'

  const [terceros, setTerceros] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [filtro, setFiltro] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [datosEditar, setDatosEditar] = useState(null);
  const [duplicarDe, setDuplicarDe] = useState(null);

  // ✅ guardamos solo el ID seleccionado (no controlamos el rowSelectionModel del grid)
  const [selectedId, setSelectedId] = useState(null);

  // responsive
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);
  const [isTablet, setIsTablet] = useState(window.innerWidth > 640 && window.innerWidth <= 980);

  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setIsMobile(w <= 640);
      setIsTablet(w > 640 && w <= 980);
    };
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const cargarTerceros = useCallback(async () => {
    try {
      const res = await api.get(`/api/terceros?tipo=${tipo}`);
      const data = res.data || [];
      setTerceros(data);
      if (tipo === 'cliente') setClientes(data);
    } catch (err) {
      console.error(`Error al cargar ${tipo}s:`, err);
    }
  }, [tipo]);

  useEffect(() => {
    cargarTerceros();
    setSelectedId(null);
  }, [cargarTerceros, location.search]);

  const placeholder =
    tipo === 'proveedor'
      ? 'Buscar por nombre, razón social, código, identificación, correo o teléfono...'
      : 'Buscar por nombre, código, identificación, correo o teléfono...';

  // 🔎 filtro
  const filtrados = useMemo(() => {
    const q = (filtro || '').toLowerCase().trim();
    if (!q) return terceros || [];
    return (terceros || []).filter(
      (t) =>
        (t.nombre || '').toLowerCase().includes(q) ||
        (t.razon_social || '').toLowerCase().includes(q) ||
        (t.codigotercero || '').toLowerCase().includes(q) ||
        (t.identificacion || '').toLowerCase().includes(q) ||
        (t.correo || '').toLowerCase().includes(q) ||
        (t.telefono || '').toLowerCase().includes(q)
    );
  }, [terceros, filtro]);

  // map para cliente padre (evita buscar en cada fila)
  const clienteNombreById = useMemo(() => {
    const m = new Map();
    (clientes || []).forEach((c) => m.set(c.idtercero, c.nombre));
    return m;
  }, [clientes]);

  const selectedTercero = useMemo(() => {
    if (!selectedId) return null;
    return (filtrados || []).find((t) => t.idtercero === selectedId) || null;
  }, [selectedId, filtrados]);

  const abrirModal = (tercero = null) => {
    setDatosEditar(tercero);
    setDuplicarDe(null);
    setShowModal(true);
  };

  const abrirDuplicado = (principal) => {
    if (tipo !== 'cliente') return;
    const esPrincipal = (principal.clasifcliente ?? 'CLIENTE') === 'CLIENTE';
    if (!esPrincipal) return;
    setDatosEditar(null);
    setDuplicarDe(principal);
    setShowModal(true);
  };

  /* ==================== CHECK & DELETE ==================== */
  const existeFacturaParaCliente = async (idtercero) => {
    try {
      const r = await api.get(`/api/terceros/${idtercero}/can-delete`);
      if (typeof r?.data?.canDelete === 'boolean') return r.data.canDelete ? false : true;
    } catch (_) {}

    const candidates = [
      `/api/factura_consolidada?Idcliente=${idtercero}&limit=1`,
      `/api/factura-consolidada?Idcliente=${idtercero}&limit=1`,
      `/api/facturas-consolidadas?Idcliente=${idtercero}&limit=1`,
      `/api/factura_consolidada/exists?Idcliente=${idtercero}`
    ];
    for (const url of candidates) {
      try {
        const res = await api.get(url);
        const data = res?.data;
        if (data == null) continue;
        if (typeof data === 'object' && 'exists' in data) return !!data.exists;
        if (Array.isArray(data)) return data.length > 0;
        if (typeof data === 'number') return data > 0;
      } catch (_) {}
    }
    return null;
  };

  const eliminarTercero = async () => {
    if (!selectedTercero) return;
    try {
      const ok = window.confirm(
        `¿Eliminar "${selectedTercero.nombre}"? Esta acción no se puede deshacer.`
      );
      if (!ok) return;

      const check = await existeFacturaParaCliente(selectedTercero.idtercero);
      if (check === true) {
        alert('No se puede eliminar: existen facturas emitidas a este tercero.');
        return;
      }

      await api.delete(`/api/terceros/${selectedTercero.idtercero}`);
      alert('Tercero eliminado.');
      await cargarTerceros();
      setSelectedId(null);
    } catch (err) {
      const status = err?.response?.status;
      const reason = err?.response?.data?.reason || '';
      const serverMsg = err?.response?.data?.error || '';

      if (status === 409 && reason === 'FACTURAS_EMITIDAS') {
        alert('No se puede eliminar: existen facturas emitidas a este tercero.');
        return;
      }
      if (status === 404) {
        alert('Tercero no encontrado.');
        return;
      }

      console.error(err);
      alert(serverMsg || 'No se pudo eliminar el tercero. Revisa la consola para más detalles.');
    }
  };

  /* ==================== COLUMNS ==================== */
  const columns = useMemo(() => {
    const base = [
      {
        field: 'codigotercero',
        headerName: 'Código',
        minWidth: 110,
        flex: 0.7,
        valueGetter: (_v, row) => row?.codigotercero || ''
      },
      {
        field: 'nombre',
        headerName: tipo === 'cliente' ? 'Nombre Cliente/Mark' : 'Nombre Comercial',
        minWidth: 260,
        flex: 1.8,
        valueGetter: (_v, row) => row?.nombre || ''
      }
    ];

    if (tipo === 'proveedor') {
      base.push(
        {
          field: 'razon_social',
          headerName: 'Razón social',
          minWidth: 260,
          flex: 1.4,
          valueGetter: (_v, row) => row?.razon_social || ''
        },
        {
          field: 'identificacion',
          headerName: 'Identificación',
          minWidth: 160,
          flex: 1,
          valueGetter: (_v, row) => row?.identificacion || ''
        },
        {
          field: 'telefono',
          headerName: 'Teléfono',
          minWidth: 150,
          flex: 1,
          valueGetter: (_v, row) => row?.telefono || ''
        },
        {
          field: 'correo',
          headerName: 'Correo',
          minWidth: 220,
          flex: 1.2,
          valueGetter: (_v, row) => row?.correo || ''
        },
        {
          field: 'direccion',
          headerName: 'Dirección',
          minWidth: 260,
          flex: 1.6,
          valueGetter: (_v, row) => row?.direccion || ''
        }
      );
    } else {
      base.push(
        {
          field: 'identificacion',
          headerName: 'Identificación',
          minWidth: 160,
          flex: 1,
          valueGetter: (_v, row) => row?.identificacion || ''
        },
        {
          field: 'telefono',
          headerName: 'Teléfono',
          minWidth: 150,
          flex: 1,
          valueGetter: (_v, row) => row?.telefono || ''
        },
        {
          field: 'correo',
          headerName: 'Correo',
          minWidth: 220,
          flex: 1.2,
          valueGetter: (_v, row) => row?.correo || ''
        },
        {
          field: 'tipo_venta_default',
          headerName: 'Tipo Venta',
          minWidth: 140,
          flex: 0.9,
          valueGetter: (_v, row) => row?.tipo_venta_default || 'NACIONAL'
        },
        {
          field: 'idcliente_padre',
          headerName: 'Cliente Principal',
          minWidth: 220,
          flex: 1.2,
          valueGetter: (_v, row) => {
            const id = row?.idcliente_padre;
            return id ? clienteNombreById.get(id) || '' : '';
          }
        }
      );
    }

    return base;
  }, [tipo, clienteNombreById]);

  /* ==================== COLUMN VISIBILITY ==================== */
  const columnVisibilityModel = useMemo(() => {
    const model = {
      codigotercero: true,
      nombre: true,
      razon_social: true,
      identificacion: true,
      telefono: true,
      correo: true,
      direccion: true,
      tipo_venta_default: true,
      idcliente_padre: true
    };

    // móvil: solo lo esencial (nombre; opcional código)
    if (isMobile) {
      model.codigotercero = false; // pon true si quieres ver código en móvil
      model.razon_social = false;
      model.identificacion = false;
      model.telefono = false;
      model.correo = false;
      model.direccion = false;
      model.tipo_venta_default = false;
      model.idcliente_padre = false;
    }

    // tablet: oculta lo pesado
    if (isTablet) {
      model.direccion = false;
      model.idcliente_padre = false;
    }

    // si es proveedor, campos de cliente no aplican
    if (tipo === 'proveedor') {
      model.tipo_venta_default = false;
      model.idcliente_padre = false;
    } else {
      model.razon_social = false;
    }

    return model;
  }, [isMobile, isTablet, tipo]);

  // ✅ handler compatible con varias versiones (array o { ids: Set })
  const handleRowSelectionChange = (next) => {
    let ids = [];

    if (Array.isArray(next)) {
      ids = next;
    } else if (next?.ids && typeof next.ids.size === 'number') {
      ids = Array.from(next.ids);
    } else if (next && typeof next === 'object') {
      // fallback por si viene raro
      try {
        ids = Array.from(next);
      } catch (_) {
        ids = [];
      }
    }

    const last = ids.length ? ids[ids.length - 1] : null;
    setSelectedId(last != null ? last : null);
  };

  return (
    <div className="terceros-page">
      <h2 className="terceros-title">{tipo === 'cliente' ? 'Clientes' : 'Proveedores'}</h2>

      <div className="terceros-toolbar">
        <select
          className="terceros-tipo"
          value={tipo}
          onChange={(e) => {
            const v = e.target.value;
            const q = new URLSearchParams(location.search);
            q.set('tipo', v);
            navigate({ search: `?${q.toString()}` }, { replace: false });
          }}
        >
          <option value="cliente">Clientes</option>
          <option value="proveedor">Proveedores</option>
        </select>

        <input
          className="terceros-search"
          type="text"
          placeholder={placeholder}
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
        />

        <div className="terceros-actions">
          <button onClick={() => abrirModal()} title={`Nuevo ${tipo}`}>
            ➕ Nuevo {tipo}
          </button>

          <button
            onClick={() => selectedTercero && abrirModal(selectedTercero)}
            disabled={!selectedTercero}
            title="Editar seleccionado"
          >
            ✏️ Editar
          </button>

          {tipo === 'cliente' && (
            <button
              onClick={() => {
                if (!selectedTercero) return;
                const esPrincipal = (selectedTercero.clasifcliente ?? 'CLIENTE') === 'CLIENTE';
                if (!esPrincipal) {
                  alert('Solo se puede duplicar desde un Cliente Principal.');
                  return;
                }
                abrirDuplicado(selectedTercero);
              }}
              disabled={!selectedTercero}
              title="Crear marcación duplicando del cliente principal seleccionado"
            >
              🧬 Duplicar
            </button>
          )}

          <button
            onClick={eliminarTercero}
            disabled={!selectedTercero}
            title="Eliminar seleccionado"
          >
            🗑️ Eliminar
          </button>
        </div>
      </div>

      <div className="terceros-grid">
        <DataGridPremium
          rows={filtrados}
          columns={columns}
          getRowId={(row) => row.idtercero}
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          density="compact"
          disableColumnMenu
          checkboxSelection
          disableMultipleRowSelection
          onRowSelectionModelChange={handleRowSelectionChange}
          onRowDoubleClick={(params) => params?.row && abrirModal(params.row)}
          columnVisibilityModel={columnVisibilityModel}
          pagination
          initialState={{
            pagination: { paginationModel: { pageSize: 25, page: 0 } },
            sorting: { sortModel: [{ field: 'nombre', sort: 'asc' }] }
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          sx={{
            border: 'none',
            backgroundColor: '#fff',
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f0f0f0' },
            '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#eef6ff !important' },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': { outline: 'none' }
          }}
        />
      </div>

      {showModal && (
        <ModalTercero
          show={showModal}
          onClose={() => {
            setShowModal(false);
            setDuplicarDe(null);
          }}
          onSave={() => {
            setShowModal(false);
            setDuplicarDe(null);
            cargarTerceros();
          }}
          tipo={tipo}
          clientes={clientes}
          datosEditar={datosEditar}
          duplicarDe={duplicarDe}
        />
      )}
    </div>
  );
}

export default TercerosForm;
